import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
  addDoc,
  writeBatch,
  getDocs,
} from 'firebase/firestore';
import { firestore } from '../../database/firebase';

const SharedTaskComponent = ({
  userRole,
  userEmail,
  canCreate = true,
  canEdit = true,
  canDelete = true,
  canAssign = false,
  assignedUsers = [],
  title = 'Tasks',
}) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userEmail) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribes = [];

    const cleanup = () => {
      unsubscribes.forEach(unsub => {
        if (typeof unsub === 'function') {
          try {
            unsub();
          } catch (e) {}
        }
      });
      unsubscribes.length = 0;
    };

    try {
      if (
        userRole === 'admin' ||
        userRole === 'businessadvisor' ||
        userRole === 'superadmin' ||
        userRole === 'superAdmin'
      ) {
        const tasksQuery = query(collection(firestore, 'tasks'), orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(
          tasksQuery,
          snapshot => {
            const allTasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTasks(allTasks);
            setLoading(false);
          },
          error => {
            const fallbackQuery = query(collection(firestore, 'tasks'));
            const unsubscribeFallback = onSnapshot(
              fallbackQuery,
              snapshot => {
                const allTasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setTasks(
                  allTasks.sort((a, b) => {
                    const aTime = a.createdAt?.toDate?.() || new Date(0);
                    const bTime = b.createdAt?.toDate?.() || new Date(0);
                    return bTime - aTime;
                  })
                );
                setLoading(false);
              },
              error => {
                setError('Failed to load tasks. Please try again.');
                setLoading(false);
              }
            );
            unsubscribes.push(unsubscribeFallback);
          }
        );
        unsubscribes.push(unsubscribe);
      } else if (userRole === 'client') {
        const assignedTasksQuery = query(
          collection(firestore, 'tasks'),
          where('assignedToEmail', '==', userEmail),
          orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(
          assignedTasksQuery,
          snapshot => {
            const assignedTasks = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              taskType: 'assigned',
            }));
            setTasks(assignedTasks);
            setLoading(false);
          },
          error => {
            const fallbackQuery = query(
              collection(firestore, 'tasks'),
              where('assignedToEmail', '==', userEmail)
            );
            const unsubscribeFallback = onSnapshot(
              fallbackQuery,
              snapshot => {
                const assignedTasks = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data(),
                  taskType: 'assigned',
                }));
                setTasks(
                  assignedTasks.sort((a, b) => {
                    const aTime = a.createdAt?.toDate?.() || new Date(0);
                    const bTime = b.createdAt?.toDate?.() || new Date(0);
                    return bTime - aTime;
                  })
                );
                setLoading(false);
              },
              error => {
                setError('Failed to load tasks. Please try again.');
                setLoading(false);
              }
            );
            unsubscribes.push(unsubscribeFallback);
          }
        );
        unsubscribes.push(unsubscribe);
      } else {
        let assignedTasks = [];
        let createdTasks = [];

        const updateTasks = () => {
          const allTasks = [...assignedTasks, ...createdTasks];
          const uniqueTasks = allTasks.filter(
            (task, index, self) => index === self.findIndex(t => t.id === task.id)
          );
          setTasks(uniqueTasks);
          setLoading(false);
        };

        const assignedTasksQuery = query(
          collection(firestore, 'tasks'),
          where('assignedToEmail', '==', userEmail),
          orderBy('createdAt', 'desc')
        );

        const unsubscribeAssigned = onSnapshot(
          assignedTasksQuery,
          snapshot => {
            assignedTasks = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              taskType: 'assigned',
            }));
            updateTasks();
          },
          error => {
            const fallbackAssignedQuery = query(
              collection(firestore, 'tasks'),
              where('assignedToEmail', '==', userEmail)
            );
            const unsubscribeAssignedFallback = onSnapshot(
              fallbackAssignedQuery,
              snapshot => {
                assignedTasks = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data(),
                  taskType: 'assigned',
                }));
                assignedTasks.sort((a, b) => {
                  const aTime = a.createdAt?.toDate?.() || new Date(0);
                  const bTime = b.createdAt?.toDate?.() || new Date(0);
                  return bTime - aTime;
                });
                updateTasks();
              },
              error => {
                toast.error('Failed to fetch assigned tasks');
              }
            );
            unsubscribes.push(unsubscribeAssignedFallback);
          }
        );
        unsubscribes.push(unsubscribeAssigned);

        const createdTasksQuery = query(
          collection(firestore, 'tasks'),
          where('createdBy', '==', userEmail),
          orderBy('createdAt', 'desc')
        );

        const unsubscribeCreated = onSnapshot(
          createdTasksQuery,
          snapshot => {
            createdTasks = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              taskType: 'created',
            }));
            updateTasks();
          },
          error => {
            const fallbackCreatedQuery = query(
              collection(firestore, 'tasks'),
              where('createdBy', '==', userEmail)
            );
            const unsubscribeCreatedFallback = onSnapshot(
              fallbackCreatedQuery,
              snapshot => {
                createdTasks = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data(),
                  taskType: 'created',
                }));
                createdTasks.sort((a, b) => {
                  const aTime = a.createdAt?.toDate?.() || new Date(0);
                  const bTime = b.createdAt?.toDate?.() || new Date(0);
                  return bTime - aTime;
                });
                updateTasks();
              },
              error => {
                toast.error('Failed to fetch created tasks');
              }
            );
            unsubscribes.push(unsubscribeCreatedFallback);
          }
        );
        unsubscribes.push(unsubscribeCreated);
      }

      return cleanup;
    } catch (error) {
      setError('Failed to load tasks. Please try again.');
      setLoading(false);
      return cleanup;
    }
  }, [userEmail, userRole]);

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      const taskRef = doc(firestore, 'tasks', taskId);
      await updateDoc(taskRef, {
        status: newStatus,
        completedAt: newStatus === 'completed' ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
        updatedBy: userEmail,
      });

      toast.success(`Task ${newStatus === 'completed' ? 'completed' : 'updated'}!`);
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const deleteTask = async taskId => {
    try {
      await deleteDoc(doc(firestore, 'tasks', taskId));
      toast.success('Task deleted successfully');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const getPriorityColor = priority => {
    switch (priority) {
      case 'high':
        return 'bg-red-50 text-red-600 border-red-200';
      case 'medium':
        return 'bg-amber-50 text-amber-600 border-amber-200';
      case 'low':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      case 'in-progress':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'pending':
        return 'bg-gray-50 text-gray-600 border-gray-200';
      case 'overdue':
        return 'bg-red-50 text-red-600 border-red-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const filteredTasks = tasks
    .filter(task => {
      if (filter !== 'all' && task.status !== filter) return false;
      if (searchTerm && !task.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const taskStats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    pending: tasks.filter(t => t.status === 'pending').length,
    inProgress: tasks.filter(t => t.status === 'in-progress').length,
    overdue: tasks.filter(t => {
      if (t.status === 'completed') return false;
      return new Date(t.dueDate) < new Date();
    }).length,
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading tasks...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <span className="text-red-600">⚠️</span>
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">Error Loading Tasks</h3>
            <p className="text-gray-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-4 shadow-lg md:p-6"
    >
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-black">{title}</h2>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-5">
        <div className="rounded-lg bg-gray-50 p-3 text-center">
          <div className="text-2xl font-bold text-black">{taskStats.total}</div>
          <div className="text-sm text-gray-600">Total</div>
        </div>
        <div className="rounded-lg bg-emerald-50 p-3 text-center">
          <div className="text-2xl font-bold text-emerald-600">{taskStats.completed}</div>
          <div className="text-sm text-gray-600">Completed</div>
        </div>
        <div className="rounded-lg bg-blue-50 p-3 text-center">
          <div className="text-2xl font-bold text-blue-600">{taskStats.inProgress}</div>
          <div className="text-sm text-gray-600">In Progress</div>
        </div>
        <div className="rounded-lg bg-amber-50 p-3 text-center">
          <div className="text-2xl font-bold text-amber-600">{taskStats.pending}</div>
          <div className="text-sm text-gray-600">Pending</div>
        </div>
        <div className="rounded-lg bg-red-50 p-3 text-center">
          <div className="text-2xl font-bold text-red-600">{taskStats.overdue}</div>
          <div className="text-sm text-gray-600">Overdue</div>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-3 sm:gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1 text-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2 pl-4 pr-4 text-sm focus:border-blue-500 focus:outline-none md:w-64"
          />
        </div>
      </div>

      <div className="space-y-4">
        {filteredTasks.length === 0 ? (
          <div className="py-12 text-center">
            <h3 className="mt-2 text-sm font-medium text-gray-900">No tasks found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filter !== 'all'
                ? 'Try adjusting your filters.'
                : 'No tasks available.'}
            </p>
          </div>
        ) : (
          filteredTasks.map(task => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg border border-gray-200 p-4 transition-shadow hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="mb-2 flex items-center gap-3">
                    <h3 className="font-medium text-black">{task.title}</h3>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getPriorityColor(task.priority)}`}
                    >
                      {task.priority}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getStatusColor(task.status)}`}
                    >
                      {task.status}
                    </span>
                  </div>
                  <p className="mb-3 text-sm text-gray-600">{task.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                    <span>
                      Assigned to: {task.assignedToName || task.assignedToEmail || task.assignedTo}
                    </span>
                    {task.createdBy &&
                      (userRole === 'admin' ||
                        userRole === 'businessadvisor' ||
                        userRole === 'superadmin' ||
                        userRole === 'superAdmin') && (
                        <span>Created by: {task.createdByName || task.createdBy}</span>
                      )}
                    {task.estimatedHours && <span>{task.estimatedHours}h</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {task.status !== 'completed' && (
                    <button
                      onClick={() => updateTaskStatus(task.id, 'completed')}
                      className="rounded-lg bg-emerald-100 p-2 text-emerald-600 transition-colors hover:bg-emerald-200"
                      title="Mark as completed"
                    >
                      ✓
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => deleteTask(task.id)}
                      className="rounded-lg bg-red-100 p-2 text-red-600 transition-colors hover:bg-red-200"
                      title="Delete task"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </motion.div>
  );
};

export default SharedTaskComponent;
